import React, { useState, useEffect } from 'react';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { Grid } from '@mui/material';
import Box from '@mui/material/Box';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import ImageListItemBar from '@mui/material/ImageListItemBar';
import ListSubheader from '@mui/material/ListSubheader';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ListItemText from '@mui/material/ListItemText';
import Skeleton from '@mui/material/Skeleton';
import Button from '@mui/material/Button';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import dayjs from 'dayjs';
import pLimit from 'p-limit';
import DOMPurify from "dompurify";
import { GetStudy, GetInstanceThumb, GetReportHtml, GetSeriesBatch, GetStudyAudit } from '../../../../api'
import ReportingData from '../Studies/Teleconsulation/Respond/ReportingData'
import { getFilename } from './fileDrop'

const limit = pLimit(2);

const SkeletonList = props =>
  <Box sx={{width: 300}}>
    <Skeleton />
    <Skeleton />
    <Skeleton />
  </Box>

export default function DetailPanelContent ({ org: orgId, row, setPdfDialogState, medDream, showPatientInfo }) {

    const [ thumbs, setThumbs ] = useState([])
    const [ studies, setStudies ] = useState()
    const [ html, setHtml ] = useState()
    const [ reportObj, setReportObj ] = useState()
    const [ expandedPrelim, setExpandedPrelim ] = useState(false)
    const [ expandedReport, setExpandedReport ] = useState(false)
    const [ auditLog, setAuditLog ] = useState()
    const PatientID = row.PatientMainDicomTags?.PatientID

    useEffect(() => {

        const org = row.associateOrg?.id || row.sentBy?.org?.id || orgId

        if (PatientID && org)
          GetStudy(null, {view: 'patient', org, PatientID}).then(setStudies).catch(e=>e)

    }, [row, PatientID, orgId])

    //const reportStudies = useMemo(() => studies && studies.filter(e => e.completeAt), [studies])

    useEffect(() => {
        const thumbArray = []
        if ("Series" in row && Array.isArray(row.Series) && row.Series.length) {
            // Check if Series are already populated (objects) or just IDs (strings)
            const firstSeries = row.Series[0]
            if (typeof firstSeries === 'string') {
                // Series are just IDs, need to fetch full data
                GetSeriesBatch(row.Series).then(seriesData => {
                    for (const series of seriesData) {
                        if (series.Instances && series.Instances.length && !thumbArray.some(obj => obj.id === series.id)) {
                            const i = thumbArray.push({
                                id: series.id,
                                name: series.MainDicomTags?.SeriesDescription,
                                modality: series.MainDicomTags?.Modality,
                                count: series.Instances.length,
                            })-1
                            limit(async () => await GetInstanceThumb(series.Instances[0].id).then(thumb => {
                                if (thumb)
                                    setThumbs(thumbs => {
                                        const copyThumbs = [...thumbs]
                                        copyThumbs[i].thumb = thumb
                                        return copyThumbs
                                    })
                            }).catch(e => e))
                        }
                    }
                    setThumbs(thumbArray)
                }).catch(e => console.error('Failed to fetch series data:', e))
            } else {
                // Series are already populated objects
                for (const series of row.Series) {
                    if (series.Instances && series.Instances.length && !thumbArray.some(obj => obj.id === series.id)) {
                        const i = thumbArray.push({
                            id: series.id,
                            name: series.MainDicomTags?.SeriesDescription,
                            modality: series.MainDicomTags?.Modality,
                            count: series.Instances.length,
                        })-1
                        limit(async () => await GetInstanceThumb(series.Instances[0].id).then(thumb => {
                            if (thumb)
                                setThumbs(thumbs => {
                                    const copyThumbs = [...thumbs]
                                    copyThumbs[i].thumb = thumb
                                    return copyThumbs
                                })
                        }).catch(e => e))
                    }
                }
                setThumbs(thumbArray)
            }
        } else {
            setThumbs([])
        }
    }, [row])

    useEffect(() => {
      GetReportHtml(row.id).then(obj => {
        setReportObj(obj)
        setHtml(obj.consult)
      }).catch(e=>e)
    }, [row.id])

    const dicomStudies = React.useMemo(() => studies?.filter(e=>e.Series?.length), [studies])

    return (
      <>
      <Stack
        sx={{ py: 2, boxSizing: 'border-box' }}
        direction="column"
      >
        <Paper sx={{ flex: 1, mx: 'auto', width: '98%', p: 1 }}>
          <Stack direction="column" spacing={1} sx={{ height: 1 }}>
            {showPatientInfo && <Container><ReportingData study={row} section="consult" hideFreeze hideConsult /></Container>}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button size="small" variant="outlined" onClick={() => GetStudyAudit(row.id).then(setAuditLog).catch(() => {})}>
                Audit Log
              </Button>
            </Box>
            <ImageList sx={{ width: '100%', maxWidth: 750 }} cols={4}>
                {thumbs.map(thumb => (
                    <ImageListItem key={thumb.id}>
                        {(thumb.thumb && <img
                            srcSet={thumb.thumb}
                            src={thumb.thumb}
                            alt={thumb.name}
                          />) ||
                          <Skeleton variant="rectangular" width={146} height={133} />
                        }
                        <ImageListItemBar
                            title={thumb.name}
                            subtitle={`${thumb.modality} ${thumb.count > 1 ? `(${thumb.count})` : ''}`}
                        />
                    </ImageListItem>
                ))}
            </ImageList>
            <Grid maxWidth="md" sx={{"& *": { margin: 0, color: '#fff !important' }, "& img": {maxHeight: '17em', maxWidth: '100%', width: 'auto', height: 'auto'}, "& h1.__root_label": {fontSize: '11pt', margin: '1em 0'} }} dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(html)}}></Grid>
            {(row.prelimReport || row.report) && (
              <Box sx={{ maxWidth: 'md', display: 'flex', gap: 1 }}>
                {row.prelimReport && !expandedPrelim && (
                  <Button variant="outlined" onClick={() => { setHtml(html + (reportObj?.prelimReport || '')); setExpandedPrelim(true); }}>Expand Prelim Report</Button>
                )}
                {row.report && !expandedReport && (
                  <Button variant="outlined" onClick={() => { setHtml(html + (reportObj?.report || '')); setExpandedReport(true); }}>Expand Consult Report</Button>
                )}
              </Box>
            )}
            {(studies && 
              <List
                sx={{ bgcolor: 'transparent' }}
                aria-labelledby="nested-list-subheader"
                subheader={
                  <ListItemButton disabled={!dicomStudies?.length} color="secondary" dense={false} component="a" sx={{px:0}} onClick={() => studies && medDream(dicomStudies.map(e=>e.MainDicomTags.StudyInstanceUID).join(','))}>
                    <ListSubheader disableSticky={true} sx={{bgcolor: 'transparent', fontSize: 16}} component="div" id="nested-list-subheader" color="primary">
                      Studies (view all)
                    </ListSubheader>
                  </ListItemButton>
                }
              >
                {studies.map(study => {
                  const date = new Date(study.MainDicomTags.StudyTimestamp)
                  const outputOptions = {
                    year: "2-digit",
                    month: "numeric",
                    day: "numeric",
                    hour: study.MainDicomTags.StudyTime ? "numeric" : undefined,
                    minute: study.MainDicomTags.StudyTime ? "numeric" : undefined,
                  }
                  const reportOutputOptions = {
                    year: "2-digit",
                    month: "numeric",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                    timeZone: study.org?.timezone || 'America/Los_Angeles'
                  }
                  const reportDate = study.completeAt && dayjs(study.completeAt.last || study.completeAt.first).toDate()
                  return (
                    <>
                      <ListItem disablePadding key={study.id}>
                        <ListItemButton dense={false} disabled={!study.Series?.length} component="a" onClick={() => medDream(study.MainDicomTags.StudyInstanceUID)}>
                          <ListItemIcon><BrokenImageIcon /></ListItemIcon>
                          <ListItemText primary={date.toLocaleString("en-US", outputOptions)} />
                        </ListItemButton>
                      </ListItem>
                      {reportDate && (
                        <ListItem sx={{ml:2}} disablePadding>
                          <ListItemButton dense={false} component="a" onClick={() => setPdfDialogState({ open: true, studyId: `${study.id}` })}>
                            <ListItemIcon><PictureAsPdfIcon /></ListItemIcon>
                            <ListItemText primary={reportDate.toLocaleString("en-US", reportOutputOptions)} />
                          </ListItemButton>
                        </ListItem>
                      )}
                    </>
                  )
                })}
              </List>
            ) ||
              <SkeletonList />
            }
            {/*(reportStudies && !!reportStudies.length &&
              <List
                sx={{ bgcolor: 'transparent' }}
                aria-labelledby="nested-list-subheader"
                subheader={
                  <ListSubheader disableSticky={true} sx={{bgcolor: 'transparent', fontSize: 16}} component="div" id="nested-list-subheader">
                    Reports
                  </ListSubheader>
                }
              >
                {reportStudies.map(study => {
                  const date = dayjs(study.completeAt.last || study.completeAt.first).toDate()
                  const outputOptions = {
                    year: "2-digit",
                    month: "numeric",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                    timeZone: study.org?.timezone || 'America/Los_Angeles'
                  }
                  return (
                    <ListItem disablePadding key={study.id}>
                      <ListItemButton dense={false} component="a" onClick={() => setPdfDialogState({ open: true, studyId: `${study.id}` })}>
                        <ListItemIcon><PictureAsPdfIcon /></ListItemIcon>
                        <ListItemText primary={date.toLocaleString("en-US", outputOptions)} />
                      </ListItemButton>
                    </ListItem>
                  )
                })}
              </List>
              )*/}
            {!!row.media?.length && (
              <List>
                <ListSubheader disableSticky={true} sx={{bgcolor: 'transparent', fontSize: 16}} component="div" id="nested-list-subheader">
                  Multimedia
                </ListSubheader>
                {row.media.map((file, index) =>
                    <ListItem disablePadding key={file}>
                      <ListItemButton component="a" href={file}>
                        <ListItemIcon>
                            <FilePresentIcon />
                        </ListItemIcon>
                        <ListItemText sx={{whiteSpace: 'pre-wrap', overflowWrap: 'break-word'}} primary={getFilename(file)} />
                      </ListItemButton>
                    </ListItem>
                )}
              </List>
            )}
          </Stack>
        </Paper>
      </Stack>
      <Dialog open={!!auditLog} onClose={() => setAuditLog()} maxWidth="sm" fullWidth>
        <DialogTitle>Audit Log</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Action</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Info</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {auditLog?.map(entry => (
                <TableRow key={entry._id}>
                  <TableCell>{entry.action}</TableCell>
                  <TableCell>{entry.user?.name || entry.user?.email || 'System'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayjs(entry.createdAt).format('MM/DD/YY h:mm a')}</TableCell>
                  <TableCell>{entry.info}</TableCell>
                </TableRow>
              ))}
              {auditLog?.length === 0 && (
                <TableRow><TableCell colSpan={3}>No audit entries found.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditLog()}>Close</Button>
        </DialogActions>
      </Dialog>
      </>
    )
  }